<template>
  <v-form ref="form" :disabled="$loader.loading">
    <h3 class="mb-6">
      Dados Pessoais
    </h3>
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.name"
          variant="outlined"
          label="Nome"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.cellphone"
          v-maska="$masks.cellphone.opts.mask"
          variant="outlined"
          label="Telefone"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.email"
          variant="outlined"
          label="E-mail"
          :rules="[rules.required, rules.email]"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.document_type"
          variant="outlined"
          label="Tipo de Documento"
          :items="[
            { title: 'CPF', value: 'cpf' },
            { title: 'Passaporte', value: 'passport' },
          ]"
          :rules="[rules.required]"
          @update:model-value="model.document = ''"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-if="model.document_type !== 'passport'"
          v-model="model.document"
          v-maska="$masks.cpf.opts.mask"
          variant="outlined"
          label="CPF"
          :rules="[rules.required, rules.cpf]"
        />
        <v-text-field
          v-else
          :model-value="model.document"
          variant="outlined"
          label="Passaporte"
          :rules="[rules.required]"
          @update:model-value="model.document = ($event ?? '').toUpperCase()"
        />
      </v-col>
    </v-row>
    <v-divider class="mt-3 mb-5" />
    <h3 v-if="model.address" class="mb-6">
      Endereço
    </h3>
    <v-row v-if="model.address">
      <v-col cols="12" md="6">
        <t-country-select v-model="model.address.country" />
      </v-col>
      <v-col v-if="brazilianAddress" cols="12" md="6">
        <v-text-field
          v-model="model.address.zipcode"
          v-maska="$masks.cep.opts.mask"
          variant="outlined"
          label="CEP"
          :rules="[rules.required, rules.cep]"
          @change="fetchAddress"
        />
      </v-col>
      <v-col v-else cols="12" md="6">
        <v-text-field
          v-model="model.address.zipcode"
          variant="outlined"
          label="CEP (Código postal)"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="model.address.neighborhood"
          variant="outlined"
          label="Bairro"
          :disabled="!model.address.zipcode && brazilianAddress"
        />
      </v-col>
      <v-col cols="6" md="4">
        <v-text-field
          v-model="model.address.state"
          variant="outlined"
          label="Estado"
          :disabled="brazilianAddress"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="model.address.city"
          variant="outlined"
          label="Cidade"
          :disabled="brazilianAddress"
        />
      </v-col>
      <v-col cols="12" md="8">
        <v-text-field
          v-model="model.address.street"
          variant="outlined"
          label="Endereço"
          :disabled="!model.address.zipcode && brazilianAddress"
        />
      </v-col>
      <v-col cols="6" md="4">
        <v-text-field
          v-model="model.address.number"
          variant="outlined"
          label="Número"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.address.complement"
          variant="outlined"
          label="Complemento"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-spacer />
      <v-col cols="12" md="2">
        <v-btn
          color="primary"
          block
          :loading="$loader.loading"
          size="x-large"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watchEffect, computed } from 'vue'
import type { VForm } from 'vuetify/lib/components'
import User from '@/types/User'
import ViaCEPService from '@/services/ViaCEPService'
import validateCPF from '@/helpers/rules/validateCPF'
import validateCEP from '@/helpers/rules/validateCEP'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import TCountrySelect from '@/components/form/TCountrySelect.vue'

const loader = useLoader()
const snackbar = useSnackBar()

interface Props {
  user?: User
}

const { user } = defineProps<Props>()

const $emit = defineEmits<{
  submit: [model: User]
}>()

function normalizeUser(u: User): User {
  return { ...u, document_type: u.document_type ?? 'cpf' }
}

const model = ref<User>(user ? normalizeUser(user) : { document_type: 'cpf' } as User)

const brazilianAddress = computed<boolean>(() => model.value.address.country === 'Brazil')

const rules = {
  required: (v: string) => (!!v || v === String(false)) || 'Campo obrigatório',
  email: (v: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'E-mail inválido',
  cpf: (v: string) => validateCPF(v),
  cep: (v: string) => validateCEP(v),
}

watchEffect(() => {
  if (user === undefined) return
  model.value = normalizeUser(user)
})

const form = useTemplateRef<VForm>('form')

async function fetchAddress() {
  const zipcode = model.value.address.zipcode.replace(/\D/g, '')
  if (zipcode.length === 8) {
    try {
      loader.increment()
      const { data } = await (new ViaCEPService().search(zipcode))
      if (!data.logradouro) throw new Error()
      model.value.address.street = data.logradouro
      model.value.address.city = data.localidade
      model.value.address.state = data.uf
      model.value.address.neighborhood = data.bairro
      model.value.address.complement = data.complemento
    } catch (error) {
      snackbar.show(
        'CEP Inválido',
        'warning',
      )
      return error
    } finally {
      loader.decrement()
    }
  }
}

async function submit() {
  if (!form.value) return

  const { valid } = await form.value.validate()

  if (!valid) {
    useSnackBar().show(
      'Verifique se os campos e cargos foram preenchidos corretamente',
      'warning',
    )
    return
  }

  $emit('submit', model.value)
}
</script>
